import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Outlet, useLocation } from "react-router-dom";
import { FiBell, FiX } from "react-icons/fi";
import { AppHeader } from "./AppHeader";
import { AppFooter } from "./AppFooter";
import { BottomNav } from "./BottomNav";
import { ToastContainer } from "../ui/Toast";
import { AnnouncementBanner } from "../ui/AnnouncementBanner";
import { PopupNotification } from "../ui/PopupNotification";
import { VerificationGate } from "../ui/VerificationGate";
import { StoryViewer } from "../ui/StoryViewer";
import { StoryCreator } from "../ui/StoryCreator";
import { connectSocket, disconnectSocket } from "../../services/socket";
import { appendMessage } from "../../store/slices/messagingSlice";
import {
  fetchMySubscription,
  selectSubStatus,
} from "../../store/slices/subscriptionSlice";
import {
  initPushNotifications,
  requestPushPermission,
} from "../../store/slices/notificationsSlice";
import { addToast } from "../../store/slices/uiSlice";
import { selectMyProfile } from "../../store/slices/profileSlice";
import {
  selectViewerOpen,
  selectCreatorOpen,
} from "../../store/slices/storiesSlice";
import { useAppDispatch, useAppSelector } from "../../hooks/useAppStore";
import { Message } from "../../types";

export const AppLayout: React.FC = () => {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const subStatus = useAppSelector(selectSubStatus);
  const { permission, pushEnabled } = useAppSelector((s) => s.notifications);
  const profile = useAppSelector(selectMyProfile);
  const viewerOpen = useAppSelector(selectViewerOpen);
  const creatorOpen = useAppSelector(selectCreatorOpen);

  const showVerificationGate =
    profile?.isSetupComplete &&
    profile?.verificationStatus === "UNVERIFIED" &&
    !location.pathname.startsWith("/verify") &&
    !location.pathname.startsWith("/profile/setup") &&
    !location.pathname.startsWith("/settings") &&
    !location.pathname.startsWith("/support");
  const [bannerDismissed, setBannerDismissed] = useState(
    () => localStorage.getItem("push_banner_dismissed") === "1",
  );

  const showBanner =
    !bannerDismissed &&
    !pushEnabled &&
    permission !== "denied" &&
    permission !== "unsupported" &&
    typeof Notification !== "undefined";

  function handleEnableNotifications() {
    dispatch(requestPushPermission());
    setBannerDismissed(true);
    localStorage.setItem("push_banner_dismissed", "1");
  }

  function handleDismissBanner() {
    setBannerDismissed(true);
    localStorage.setItem("push_banner_dismissed", "1");
  }

  useEffect(() => {
    if (subStatus === "idle") dispatch(fetchMySubscription());
  }, [dispatch, subStatus]);

  useEffect(() => {
    // AppLayout only renders behind ProtectedRoute, so a session cookie is
    // already expected to exist — the socket handshake will fail auth on its
    // own if it doesn't.
    const socket = connectSocket();

    socket.on("message:new", (message: Message) => {
      dispatch(
        appendMessage({ conversationId: message.conversationId, message }),
      );
    });

    socket.on("notification:roses", (payload: { roses: number }) => {
      dispatch(
        addToast({
          message: `🌹 ${payload.roses} rose${payload.roses === 1 ? "" : "s"} have been added to your account!`,
          type: "success",
        }),
      );
    });

    socket.on("notification:subscription", (payload: { planType: string }) => {
      dispatch(
        addToast({
          message: `🎉 Your ${payload.planType} subscription has been activated!`,
          type: "success",
        }),
      );
    });

    return () => {
      socket.off("message:new");
      socket.off("notification:roses");
      socket.off("notification:subscription");
      disconnectSocket();
    };
  }, [dispatch]);

  // Register service worker and subscribe to push notifications
  useEffect(() => {
    dispatch(initPushNotifications());
  }, [dispatch]);

  return (
    <div className="min-h-screen bg-charcoal font-body flex flex-col">
      <AnnouncementBanner />

      {/* Push notification permission banner */}
      {showBanner && (
        <div className="fixed bottom-20 left-0 right-0 z-40 flex justify-center px-4 pointer-events-none">
          <div className="pointer-events-auto flex items-center gap-3 bg-[#1a1818] border border-white/10 rounded-2xl shadow-xl px-4 py-3 max-w-sm w-full">
            <div className="w-9 h-9 rounded-xl bg-magenta/10 flex items-center justify-center flex-shrink-0">
              <FiBell className="w-4 h-4 text-magenta" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-offwhite text-sm font-medium leading-tight">
                Enable notifications
              </p>
              <p className="text-offwhite/50 text-xs mt-0.5 leading-tight">
                Get alerts for messages, roses &amp; more
              </p>
            </div>
            <button
              onClick={handleEnableNotifications}
              className="flex-shrink-0 bg-magenta text-white text-xs font-semibold px-3 py-1.5 rounded-xl hover:bg-magenta/90 transition-colors"
            >
              Enable
            </button>
            <button
              onClick={handleDismissBanner}
              className="flex-shrink-0 text-offwhite/30 hover:text-offwhite/60 transition-colors"
              aria-label="Dismiss"
            >
              <FiX className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      <AppHeader />
      <main className="flex-1 pt-14 lg:pt-16">
        {showVerificationGate && <VerificationGate />}
        <Outlet />
      </main>
      <AppFooter />
      <BottomNav />
      <ToastContainer />
      <PopupNotification />
      {viewerOpen && createPortal(<StoryViewer />, document.body)}
      {creatorOpen && createPortal(<StoryCreator />, document.body)}
    </div>
  );
};
